import { Injectable } from "@angular/core";
import { PoTableColumnSort, PoTableColumnSortType } from "@po-ui/ng-components";
import { DependencyTrackVulnerabilityDetails } from "../../interfaces/dependency-track-vulnerability-details";

@Injectable({
  providedIn: "root",
})
export class DependencyTrackTableSortService {
  constructor() {}

  sort(
    items: DependencyTrackVulnerabilityDetails,
    columnSort: PoTableColumnSort
  ): DependencyTrackVulnerabilityDetails {
    if (!items || !items.details?.findings?.length) {
      return items;
    }

    if (!columnSort || !columnSort?.column) {
      return items;
    }

    const findings = [...items.details.findings];
    const sortedFindings = findings.sort((value, valueToCompare) => {
      const property = columnSort?.column?.property;
      const type = columnSort.type;
      const numericColumns = new Set([
        "severityRank",
        "cvssV3BaseScore",
        "epssScore",
        "epssPercentile",
        "cweId",
      ]);

      if (!property) {
        return 0;
      }

      let fieldValue: any;
      let fieldValueCompare: any;

      if (property.split(".").length > 1) {
        const propertySplitedFirst = property.split(".")[0];
        const propertySplitedLast = property.split(".")[1];

        fieldValue = this.getNestedPropertyValue(
          value,
          propertySplitedFirst,
          propertySplitedLast
        );
        fieldValueCompare = this.getNestedPropertyValue(
          valueToCompare,
          propertySplitedFirst,
          propertySplitedLast
        );
      } else {
        fieldValue = this.getPropertyValue(value, property);
        fieldValueCompare = this.getPropertyValue(valueToCompare, property);
      }

      if (fieldValue == null && fieldValueCompare == null) return 0;
      if (fieldValue == null)
        return type === PoTableColumnSortType.Ascending ? 1 : -1;
      if (fieldValueCompare == null)
        return type === PoTableColumnSortType.Ascending ? -1 : 1;

      if (numericColumns.has(property)) {
        fieldValue = parseFloat(fieldValue) || 0;
        fieldValueCompare = parseFloat(fieldValueCompare) || 0;
      } else {
        fieldValue = String(fieldValue).toLowerCase();
        fieldValueCompare = String(fieldValueCompare).toLowerCase();
      }

      if (fieldValue < fieldValueCompare) {
        return type === PoTableColumnSortType.Ascending ? -1 : 1;
      }

      if (fieldValue > fieldValueCompare) {
        return type === PoTableColumnSortType.Ascending ? 1 : -1;
      }

      return 0;
    });

    return {
      ...items,
      details: {
        ...items.details,
        findings: sortedFindings,
      },
    };
  }

  private getNestedPropertyValue(
    obj: any,
    firstProperty: string,
    secondProperty: string
  ): any {
    return obj?.[firstProperty]?.[secondProperty];
  }

  private getPropertyValue(obj: any, property: string): any {
    switch (property) {
      case "vulnerability":
        return obj?.vulnerability?.vulnId;
      case "name":
        return obj?.vulnerability?.cweName || obj?.vulnerability?.vulnId;
      case "severity":
        return obj?.vulnerability?.severity;
      case "dependency":
        return obj?.component?.group
          ? `${obj.component.group}/${obj.component.name}`
          : obj?.component?.name;
      case "version":
        return obj?.component?.version;
      case "lastVersion":
        return obj?.component?.latestVersion;
      case "classifiedBy":
        return obj?.vulnerability?.source;
      case "classifiedOn":
        return obj?.attribution?.attributedOn;
      case "title":
        return obj?.vulnerability?.title;
      case "description":
        return obj?.vulnerability?.description;
      default:
        return obj?.[property];
    }
  }
}
